import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Collapse, Container, styled, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import formatISODuration from 'date-fns/formatISODuration/index';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { scroller } from 'react-scroll';

import ChevronTopIcon from '~/assets/icons/ChevronTopIcon';
import PathwaySmallIcon from '~/assets/icons/PathwaySmallIcon';
import CustomBlocks from '~/blocks/CustomBlocks/CustomBlocks';
import HeaderWithExpert from '~/blocks/HeaderWithExpert';
import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import PathwayItems from '~/blocks/PathwayItems';
import { Crumb } from '~/components/Breadcrumbs/Breadcrumbs';
import JSBlock from '~/components/JSBlock/JSBlock';
import Loader from '~/components/Loader/Loader';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import VideoPlayerLayout, { VideoOverlay } from '~/components/VideoPlayerLayout/VideoPlayerLayout';
import { DEFAULT_DISCOVER_URL, PRIORITY_PATHWAY_SLUG_COOKIE_NAME, RM_PAGES } from '~/const';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { useRmMetadata } from '~/context/RmContext';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import {
  CHECK_IF_VIDEO_EXISTS_IN_WATCHLIST,
  GET_RATING_FOR_ENTITY,
  GET_RELATED_VIDEOS,
  GET_VIDEO_PROGRESS_LIST,
} from '~/graphql/videos';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useAuth from '~/hooks/useAuth';
import useContentAvailability from '~/hooks/useContentAvailability';
import useCustomBlocks from '~/hooks/useCustomBlocks';
import useIsSSR from '~/hooks/useIsSSR';
import useOnboarding from '~/hooks/useOnboarding';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { useModalContext } from '~/modals/Modal';
import { Modals } from '~/modals/types';
import PostVideoQuestion, {
  StyledAnswerButton,
} from '~/pages/SingleVideoPage/PostVideoQuestion/PostVideoQuestion';
import VideoPlayerNavigation from '~/pages/SingleVideoPage/VideoPlayerNavigation';
import AslBanner from '~/pages/SingleVideoPage/components/AslBanner/AslBanner';
import ImageBackground from '~/pages/SingleVideoPage/components/ImageBackground/ImageBackground';
import JoinNow from '~/pages/SingleVideoPage/components/JoinNow';
import PathwayInfoRow from '~/pages/SingleVideoPage/components/PathwayInfoRow';
import RelatedVideos from '~/pages/SingleVideoPage/components/RelatedVideos';
import VideoLoggedOutPreview from '~/pages/SingleVideoPage/components/VideoLoggedOutPreview/VideoLoggedOutPreview';
import VideoPageSeoBlock from '~/pages/SingleVideoPage/components/VideoPageSeoBlock/VideoPageSeoBlock';
import VideoTabsContainer from '~/pages/SingleVideoPage/components/VideoTabsContainer/VideoTabsContainer';
import VideoTitle from '~/pages/SingleVideoPage/components/VideoTitle/VideoTitle';
import {
  hasCustomLogoByVideoSlug,
  PAYWALLED_CONTENT_SELECTORS,
  TabsEnum,
  VIDEO_PAGE_CUSTOM_BLOCKS_URL,
  VIDEO_PAGE_EVENTS,
} from '~/pages/SingleVideoPage/const';
import { getTabs } from '~/pages/SingleVideoPage/helpers';
import usePathways from '~/pages/SingleVideoPage/hooks/usePathways';
import useQuestion from '~/pages/SingleVideoPage/hooks/useQuestion';
import useRelatedAssignment from '~/pages/SingleVideoPage/hooks/useRelatedAssignment';
import useTrackUserProgress from '~/pages/SingleVideoPage/hooks/useTrackUserProgress';
import useVideoPlayer from '~/pages/SingleVideoPage/hooks/useVideoPlayer';
import useVideoReview from '~/pages/SingleVideoPage/hooks/useVideoReview';
import { PATHWAY_ITEM_TYPES } from '~/pages/const';
import {
  buildCaseStudyRoute,
  buildPathwayRoute,
  buildPathwayTestRoute,
  DISCOVER_PATHWAYS_ROUTE,
  DISCOVER_VIDEOS_ROUTE,
  LEARNER_AL_ROUTE,
} from '~/routes';
import {
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '~/theme';
import {
  Maybe,
  Query,
  QueryGetListReviewsForEntityArgs,
  QueryListVideoProgressArgs,
  QueryListVideosYouMightAlsoLikeArgs,
  QueryVideoExistsInUserWatchListArgs,
  ReviewableType,
} from '~/types';
import Cookie from '~/utils/cookies';
import getPageTitleWithTenantName from '~/utils/getPageTitleWithTenantName';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';
import { replaceQuotes } from '~/utils/stringUtils';
import { formatProgressData } from '~/utils/videoProgressUtils';

const getVideoPageJsonData = ({
  title = '',
  description = '',
  keyLearningObjectives = [],
  duration,
}: {
  title?: string;
  description?: string;
  keyLearningObjectives?: (string | undefined)[];
  duration: string;
}) => {
  const _title = replaceQuotes(title);
  const _description = replaceQuotes(description);
  const _keyLearningObjectives = keyLearningObjectives.map((item) =>
    item ? replaceQuotes(item) : item,
  );
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: _title,
    description: _description,
    provider: {
      '@type': 'Organization',
      name: 'Finance Unlocked',
      sameAs: 'https://financeunlocked.com',
    },
    teaches: _keyLearningObjectives.join(', '),
    learningResourceType: 'video',
    isAccessibleForFree: false,
    hasPart: [
      {
        '@type': 'WebPageElement',
        isAccessibleForFree: true,
        cssSelector: `.${PAYWALLED_CONTENT_SELECTORS.OVERVIEW}`,
      },
      {
        '@type': 'WebPageElement',
        isAccessibleForFree: true,
        cssSelector: `.${PAYWALLED_CONTENT_SELECTORS.SUMMARY}`,
      },
      {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: `.${PAYWALLED_CONTENT_SELECTORS.VIDEO_PLAY_BTN}`,
      },
      {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: `.${PAYWALLED_CONTENT_SELECTORS.VIDEO_PLAY_BTN_SMALL}`,
      },
      {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: `.${PAYWALLED_CONTENT_SELECTORS.JOIN_US}`,
      },
    ],
    offers: [
      {
        '@type': 'Offer',
        category: 'Paid',
      },
    ],
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseSchedule: {
          '@type': 'Schedule',
          repeatCount: 4,
          repeatFrequency: 'Monthly',
        },
        courseWorkload: duration,
      },
    ],
  });
};

interface SingleVideoPageProps {
  isXApiPage?: boolean;
  waitForUser?: boolean;
  bumperUrl?: string | null;
  setIsHeaderHidden?: Dispatch<SetStateAction<boolean>>;
}

const SingleVideoPage: FC<SingleVideoPageProps> = ({
  isXApiPage = false,
  waitForUser,
  bumperUrl,
  setIsHeaderHidden,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const withoutNavigation = useWithoutNavigation();
  const location = useLocation();

  const { tenant } = useTenantContext();
  const { userInfo, userLoading, hasAccessToContent } = useUserInfoContext();
  const { t } = useTenantTranslation();
  const navigate = useNavigate();
  const isMobile = !useIsWidthUp('md');
  const { isLoading, isAuthenticated } = useAuth();
  const isSSR = useIsSSR();
  const playerWrapperRef = useRef<HTMLElement>(null);
  const isSsku = useIsSsku();
  const { openModal } = useModalContext();
  const { onboardingComplete, onboardingShowed, handleStep, learnerGoals, goalOptions } =
    useOnboarding();
  const [withoutAslAnimation, setWithoutAslAnimation] = useState(true);
  const params = useParams();
  const contentAvailability = useContentAvailability();

  const tenantTitle = tenant?.title;
  const hasAccess = hasAccessToContent || isXApiPage;
  const noAuth = !isAuthenticated || !hasAccess;
  const noAccess = !isXApiPage && noAuth;
  const handleClickExpert = () => {
    if (hasAccess) {
      setTabIndex(tabs[TabsEnum.Expert]);
      setTimeout(() => {
        scroller.scrollTo(isMobile ? 'expertSection' : `simple-tab-${tabs[TabsEnum.Expert]}`, {
          duration: 1000,
          delay: 0,
          smooth: 'easeInOutQuart',
          offset: -100,
        });
      }, 250);
    } else {
      scroller.scrollTo('expertSection', {
        duration: 1000,
        delay: 150,
        smooth: 'easeInOutQuart',
        offset: -150,
      });
    }
  };

  const {
    video,
    loadingVideo,
    videoLoaded,
    videoError,
    play,
    goToChapter,
    videoFinished,
    setVideoFinished,
    playerRef,
    verseVideoId,
    loadingVerseVideoId,
    isVideoPlaying,
    pipControls,
  } = useVideoPlayer({ playerWrapperRef, bumperUrl });

  const videoId = video?.id ? video.id : '';
  const noVideoLoaded = !videoId || loadingVideo;
  const availableForFree = Boolean(video?.free_for_all);
  const videoPathway = video?.pathways && video?.pathways[0];
  const tabs = useMemo(() => getTabs(video), [video]);

  const {
    availableQuiz,
    pathways,
    openedPathway,
    isLastVideo,
    handleTogglePathway,
    handleOpenPathway,
    pathwaysLoading,
    pathwaysLoaded,
    nextItem: nextPathwayItem,
    videoIdsFromAllPathways,
  } = usePathways(videoId);

  const { question, refetchQuestion } = useQuestion(videoId);
  useTrackUserProgress(videoId);

  const handleOpenComments = () => {
    if (!isSsku) {
      setTabIndex(tabs[TabsEnum.Reviews]);

      if (!isVideoPlaying) {
        setTimeout(() => {
          scroller.scrollTo('commentForm', {
            duration: 1000,
            delay: 500,
            smooth: 'easeInOutQuart',
            offset: -150,
          });
        });
      }
    }
  };

  const videoReview = useVideoReview({
    videoId,
    isXApiPage: isXApiPage || withoutNavigation,
  });

  const {
    data: videoProgressData,
    called: videoProgressDataCalled,
    loading: videoProgressDataLoading,
  } = useQuery<Pick<Query, 'listVideoProgress'>, QueryListVideoProgressArgs>(
    GET_VIDEO_PROGRESS_LIST,
    {
      variables: {
        filter: {
          user_id: userInfo?.id || '',
          video_ids: [...videoIdsFromAllPathways, videoId],
        },
      },
      skip: !userInfo?.id || !videoId,
      fetchPolicy: 'network-only',
    },
  );
  const videoProgressDataLoaded = videoProgressDataCalled && !videoProgressDataLoading;

  const progressData = formatProgressData(videoProgressData?.listVideoProgress);
  const videoProgress =
    videoProgressData?.listVideoProgress?.find((item) => item?.video?.id === videoId) || null;

  const { data: relatedVideosData, loading: loadingRelatedVideos } = useQuery<
    Pick<Query, 'listVideosYouMightAlsoLike'>,
    QueryListVideosYouMightAlsoLikeArgs
  >(GET_RELATED_VIDEOS, {
    variables: {
      filter: {
        video_id: [videoId],
        user_id: userInfo?.id ? [userInfo.id] : null,
        include_test_publish_status: true,
      },
    },
    skip: isXApiPage || noVideoLoaded || isSSR,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const relatedVideosToDisplay = relatedVideosData?.listVideosYouMightAlsoLike
    ? relatedVideosData?.listVideosYouMightAlsoLike
        .filter(
          (item) => nextPathwayItem?.__typename === 'Video' && item?.id !== nextPathwayItem?.id,
        )
        .slice(0, 8)
    : [];

  const { data: existsInWatchlistData } = useQuery<
    Pick<Query, 'VideoExistsInUserWatchList'>,
    QueryVideoExistsInUserWatchListArgs
  >(CHECK_IF_VIDEO_EXISTS_IN_WATCHLIST, {
    variables: {
      filter: {
        user_id: userInfo?.id ? userInfo.id : '',
        video_id: videoId,
      },
    },
    skip: isXApiPage || !hasAccess || noVideoLoaded,
    fetchPolicy: 'network-only',
  });

  const { blocks: commonBlocks } = useCustomBlocks(VIDEO_PAGE_CUSTOM_BLOCKS_URL, {
    skip: hasAccess || isXApiPage || noVideoLoaded || isLoading,
  });

  const { blocks: logoutPageBlocks } = useCustomBlocks(location.pathname + '?logout=true', {
    skip: hasAccess || isXApiPage || noVideoLoaded || isLoading,
  });

  const { data: reviewsList } = useQuery<
    Pick<Query, 'getListReviewsForEntity'>,
    QueryGetListReviewsForEntityArgs
  >(GET_RATING_FOR_ENTITY, {
    variables: {
      entity_type: ReviewableType.Video,
      entity_id: Number(videoId),
      pagination_option: {
        page: 1,
        count_items_on_page: 10,
      },
      include_test_publish_status: true,
    },
    skip: !videoId || !isAuthenticated || isXApiPage,
  });

  const videoCompleted = !!videoProgress?.completed;
  // asl will be loaded only for completed videos
  const relatedAsl = useRelatedAssignment(videoId, isXApiPage || !isAuthenticated, videoFinished);
  const existsInWatchlist = !!existsInWatchlistData?.VideoExistsInUserWatchList?.exists;

  useEffect(() => {
    if (videoCompleted) {
      return;
    }

    if (videoFinished) {
      setWithoutAslAnimation(false);
    }
  }, [videoFinished, videoCompleted]);

  //reset selected tab on video change
  useEffect(() => {
    setTabIndex(0);
    setWithoutAslAnimation(true);
  }, [videoId]);

  useRmMetadata({ page: RM_PAGES.VideoPage });

  let key_learning_objectives = video?.key_learning_objectives?.map((item) => item?.title);
  const duration = video?.duration || 0;

  const scriptData = {
    customScriptType: 'application/ld+json',
    js_code: getVideoPageJsonData({
      title: video?.title,
      description: video?.description,
      keyLearningObjectives: key_learning_objectives,
      duration: formatISODuration({ seconds: duration }),
    }),
  };
  const rating = reviewsList?.getListReviewsForEntity?.likes_ratio;

  const relatedAslLoaded = relatedAsl.called && !relatedAsl.loading;
  const hasAslBanner =
    isAuthenticated && !isXApiPage && relatedAslLoaded && !!relatedAsl.assignment;
  const showAslBanner = hasAslBanner && (videoCompleted || videoFinished);
  const shouldOpenPathway = videoProgressDataLoaded && relatedAslLoaded && !hasAslBanner;

  useEffect(() => {
    if (!pathwaysLoaded || !Array.isArray(pathways) || !shouldOpenPathway) {
      return;
    }

    const firstPathway = pathways[0];

    if (!!firstPathway) {
      handleOpenPathway(firstPathway);
    }
  }, [pathwaysLoaded, shouldOpenPathway, pathways, handleOpenPathway]);

  useEffect(() => {
    if (!onboardingComplete && !onboardingShowed && isSsku && videoFinished) {
      openModal?.(Modals.OnboardingModal);
    }
  }, [
    videoFinished,
    onboardingShowed,
    openModal,
    onboardingComplete,
    handleStep,
    learnerGoals,
    goalOptions,
    isSsku,
  ]);

  if (waitForUser) {
    return (
      <>
        {video ? <VideoPageSeoBlock isXApiPage={isXApiPage} video={video} /> : null}
        <Loader />
      </>
    );
  }

  if (videoError) {
    return (
      <ErrorWrapper>
        <Typography variant="h1">
          {t('error.error')}: {videoError?.message}
        </Typography>
      </ErrorWrapper>
    );
  }

  const headerImage = !pathwaysLoading ? pathways?.[0]?.image_url || video?.preview_url || '' : '';

  const renderChildren = ({ setShowBumper }) => {
    const showQuizBanner = isLastVideo && availableQuiz && !withoutNavigation;
    const showNextBanner =
      !showQuizBanner && nextPathwayItem && !showAslBanner && !relatedAsl.loading;

    const nextItemLink =
      nextPathwayItem?.__typename === 'Video'
        ? `/videos/${nextPathwayItem?.video_url}`
        : buildCaseStudyRoute(openedPathway?.slug, nextPathwayItem?.id);

    const replayHandler = (e) => {
      e.stopPropagation();
      goToChapter('0');
      setShowBumper(false);
      setVideoFinished(false);
      sendAnalyticEvent(VIDEO_PAGE_EVENTS.replay);
      refetchQuestion();
    };

    const goToNextItem = () => {
      setVideoFinished(false);

      if (nextPathwayItem?.__typename === 'Video') {
        sendAnalyticEvent(VIDEO_PAGE_EVENTS.nextVideo);
      }
      navigate(nextItemLink);
    };

    const onTakeQuiz = () =>
      navigate(buildPathwayTestRoute(openedPathway?.slug, availableQuiz?.id));

    const buttonAfterQuiz =
      isLastVideo && availableQuiz && !withoutNavigation ? (
        <StyledAnswerButton data-testid="goToTestAfterQuiz" onClick={onTakeQuiz}>
          {t('page.video.takeTest')}
        </StyledAnswerButton>
      ) : nextPathwayItem && !showAslBanner ? (
        <StyledAnswerButton
          data-testid="goToVideoAfterQuiz"
          onClick={() => {
            setVideoFinished(false);
            if (openedPathway && openedPathway?.slug) {
              Cookie.set(PRIORITY_PATHWAY_SLUG_COOKIE_NAME, openedPathway.slug);
            }
            navigate(nextItemLink);
            sendAnalyticEvent(VIDEO_PAGE_EVENTS.continuePostVideo);
          }}
        >
          {t('page.video.continueLearning')}
        </StyledAnswerButton>
      ) : null;

    if (pipControls.active) {
      return null;
    }

    if (videoFinished) {
      const nextItem = !!showQuizBanner ? availableQuiz : nextPathwayItem;
      const continueLearning =
        nextItem?.__typename === 'Video' || nextItem?.__typename === 'CaseStudy'
          ? goToNextItem
          : nextItem?.__typename === 'Quiz'
          ? onTakeQuiz
          : undefined;

      const videoNavigation = (
        <VideoPlayerNavigation
          showBanner={contentAvailability.pathways && (!!showQuizBanner || !!showNextBanner)}
          nextItem={!!showQuizBanner ? availableQuiz : nextPathwayItem}
          replayHandler={replayHandler}
          onContinueClick={continueLearning}
        />
      );

      return (
        <VideoOverlay transparent={!question}>
          {question ? (
            <PostVideoQuestion
              question={question}
              videoId={videoId}
              renderNavigation={videoNavigation}
              renderButton={buttonAfterQuiz}
              continueLearning={continueLearning}
            />
          ) : (
            videoNavigation
          )}
        </VideoOverlay>
      );
    }

    return null;
  };

  const playerInfoLayout = (
    <>
      {showAslBanner && relatedAsl.watched && (
        <AslBanner
          aslLink={`${LEARNER_AL_ROUTE}?assignment=${relatedAsl?.assignment?.id}`}
          videoTitle={`${t('common.testAvailable')}: ${t('page.video.applyWhatYouHaveLearned')}`}
          aslTitle={relatedAsl?.assignment?.title}
          buttonLink={`${LEARNER_AL_ROUTE}/${relatedAsl?.assignment?.id}/test`}
          buttonTitle={t('page.video.takeTest')}
          withoutAnimation={withoutAslAnimation}
        />
      )}
      {showAslBanner && relatedAsl.nextVideo && (
        <AslBanner
          aslLink={`${LEARNER_AL_ROUTE}?assignment=${relatedAsl?.assignment?.id}`}
          aslTitle={relatedAsl?.assignment?.title}
          videoTitle={`${t('page.video.nextVideo')}: ${relatedAsl?.nextVideo?.title}`}
          buttonLink={`/videos/${relatedAsl?.nextVideo?.video_url}`}
          buttonTitle={t('common.continueAssignment')}
          withoutAnimation={withoutAslAnimation}
        />
      )}
      <VideoTitle
        title={video?.title}
        isXApiPage={isXApiPage}
        existsInWatchlist={existsInWatchlist}
        videoId={videoId}
        duration={duration}
        rating={rating}
        progress={videoProgress?.progress_percentage}
        isCompleted={videoProgress?.completed}
        availableForFree={availableForFree}
        videoReview={videoReview}
        handleOpenComments={handleOpenComments}
        expertId={video?.user?.id}
      />
      {!userInfo && availableForFree && videoPathway && !isXApiPage && (
        <>
          <Divider />
          <PathwayInfoRow name={videoPathway?.name} slug={videoPathway?.slug} />
        </>
      )}
      {contentAvailability.pathways && userInfo && !isXApiPage && !!pathways?.length && (
        <PathwaysWrapper>
          {pathways?.map((pathway, index) => {
            const currentVideoIndex =
              (pathway?.videos?.findIndex((item) => item?.id === videoId) || 0) + 1;
            const totalVideos = pathway?.count_videos;

            return (
              <PathwayWrapper
                data-testid="pathwayBlock"
                key={index}
                opened={pathway?.id === openedPathway?.id}
              >
                <CollapseHead data-testid="pathwayTitle">
                  <StyledPathwaySmallIcon onClick={() => handleTogglePathway(pathway)} />
                  <PathwayTitle onClick={() => handleTogglePathway(pathway)}>
                    {t('page.video.videoModuleIsPart', {
                      current: currentVideoIndex,
                      total: totalVideos,
                    })}
                    &nbsp;
                    <PathwayName data-testid="pathwayName" to={buildPathwayRoute(pathway?.slug)}>
                      {pathway?.name}&nbsp;
                    </PathwayName>
                    {t('page.pathway.pathway')}
                  </PathwayTitle>
                  <StyledChevron
                    data-testid="pathwayCollapseArrow"
                    onClick={() => handleTogglePathway(pathway)}
                    reversed={openedPathway?.id !== pathway?.id}
                  />
                </CollapseHead>
                <Collapse in={openedPathway?.id === pathway?.id} data-testid="pathwayCollapse">
                  <ListOfVideosWrapper>
                    <PathwayItems
                      nextItemId={videoId}
                      nextItemType={PATHWAY_ITEM_TYPES.Video}
                      pathwayItems={pathway?.items || []}
                      progressData={progressData}
                      quizes={withoutNavigation ? null : pathway?.quizes || []}
                      availableQuizes={pathway?.available_quizes || []}
                      pathway={pathway}
                      handleClickOnActive={() => {
                        play();
                        scroller.scrollTo('playerWrap', {
                          duration: 1000,
                          smooth: 'easeInOutQuart',
                          offset: -80,
                        });
                      }}
                    />
                  </ListOfVideosWrapper>
                </Collapse>
              </PathwayWrapper>
            );
          })}
        </PathwaysWrapper>
      )}
    </>
  );

  const pathwayCrumb = openedPathway && {
    title: openedPathway.name,
    url: buildPathwayRoute(openedPathway.slug),
  };

  const crumbs: Maybe<Crumb[]> = !contentAvailability.pathways
    ? null
    : pathwayCrumb
    ? [
        {
          title: t('page.discover.discover'),
          url: DEFAULT_DISCOVER_URL,
        },
        {
          title: t('page.discover.pathways'),
          url: DISCOVER_PATHWAYS_ROUTE,
        },
        pathwayCrumb,
        {
          title: video?.title || '',
        },
      ]
    : [
        {
          title: t('page.discover.discover'),
          url: DEFAULT_DISCOVER_URL,
        },
        {
          title: t('page.discover.videos'),
          url: DISCOVER_VIDEOS_ROUTE,
        },
        {
          title: video?.title || '',
        },
      ];

  return videoLoaded && !video ? (
    <RouteWithCode code={404}>
      <NotFoundPage />
    </RouteWithCode>
  ) : (
    <>
      <VideoPageSeoBlock isXApiPage={isXApiPage} video={video} />
      {video?.title && tenantTitle && (
        <Helmet>
          <title>{getPageTitleWithTenantName(video?.title, tenantTitle)}</title>
        </Helmet>
      )}
      <JSBlock data={scriptData} />
      <Wrapper>
        <ImageBackground hasSubscription={hasAccess} preview={headerImage} title={video?.title} />
        <StyledHeader
          crumbs={crumbs}
          loading={loadingVideo}
          title={video?.title || ''}
          description={video?.description || ''}
          user={video?.user}
          categories={video?.categories}
          difficulty={video?.difficulty}
          restrictNavigation={isXApiPage || withoutNavigation}
          handleClickExpert={handleClickExpert}
          followAnalyticEvent={VIDEO_PAGE_EVENTS.follow}
        />
        {!hasAccess && availableForFree && (
          <JoinNowBlockContainer>
            <JoinNow
              showForDanske={hasCustomLogoByVideoSlug(params.slug)}
              availableForFree={availableForFree}
              loading={loadingVideo || userLoading}
            />
          </JoinNowBlockContainer>
        )}
        <VideoWrap>
          <VideoBlock isXApiPage={isXApiPage} hasSubscription={hasAccess}>
            <BlockWrap id="playerWrap">
              {hasAccess && Boolean(verseVideoId) && (
                <>
                  <VideoPlayerLayout
                    loadingVerseVideoId={loadingVerseVideoId}
                    rating={rating}
                    video={video}
                    playerRef={playerRef}
                    bumperUrl={bumperUrl}
                    videoReview={videoReview}
                    playerWrapperRef={playerWrapperRef}
                    videoFinished={videoFinished}
                    pipControls={pipControls}
                    question={question}
                    onThumbClick={handleOpenComments}
                    verseVideoId={verseVideoId}
                    playerInfo={playerInfoLayout}
                    setIsHeaderHidden={setIsHeaderHidden}
                    renderChildren={renderChildren}
                    expandAnchorId="headerWithExpert"
                  />
                </>
              )}
              {!hasAccess && availableForFree && Boolean(verseVideoId) && (
                <>
                  <VideoPlayerLayout
                    rating={rating}
                    isXApiPage={isXApiPage}
                    video={video}
                    playerRef={playerRef}
                    bumperUrl={bumperUrl}
                    videoReview={videoReview}
                    playerWrapperRef={playerWrapperRef}
                    videoFinished={videoFinished}
                    pipControls={pipControls}
                    question={question}
                    onThumbClick={handleOpenComments}
                    verseVideoId={verseVideoId}
                    playerInfo={playerInfoLayout}
                    renderChildren={renderChildren}
                    expandAnchorId="headerWithExpert"
                  />
                </>
              )}
              {!hasAccess && !availableForFree && (
                <VideoLoggedOutPreview
                  loading={loadingVideo}
                  id={videoId}
                  title={video?.title}
                  previewUrl={video?.preview_url}
                  duration={duration}
                />
              )}
              <VideoTabsContainer
                loading={loadingVideo || userLoading}
                showTabs={!noAccess}
                video={video}
                isXApiPage={isXApiPage}
                userId={userInfo?.id}
                tabs={tabs}
                tabIndex={tabIndex}
                setTabIndex={setTabIndex}
                availableForFree={availableForFree}
                videoReview={videoReview}
              />
            </BlockWrap>
          </VideoBlock>
        </VideoWrap>
        {!isXApiPage && !withoutNavigation && (
          <RelatedVideos videos={relatedVideosToDisplay} loading={loadingRelatedVideos || isSSR} />
        )}
        {commonBlocks.length > 0 && <CustomBlocks blocks={commonBlocks} />}
        {logoutPageBlocks.length > 0 && <CustomBlocks blocks={logoutPageBlocks} />}
      </Wrapper>
    </>
  );
};

export default SingleVideoPage;

const PathwaysWrapper = styled('div')`
  margin-top: ${SUBMIDDLE_MARGIN_PX};
  padding: ${SUBMIDDLE_MARGIN_PX} 0 0;
  border-bottom-left-radius: ${MIDDLE_RADIUS_PX};
  border-bottom-right-radius: ${MIDDLE_RADIUS_PX};
  border-top: 1px solid ${({ theme }) => alpha(theme.palette.common.divider.main, 0.3)};
`;
const PathwayWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'opened',
})<{
  opened: boolean;
}>`
  transition: margin-bottom 0.3s;
  margin-bottom: ${({ opened }) => opened && SMALL_MARGIN_PX};

  &:not(:last-child) {
    margin-bottom: ${({ opened }) => opened && MIDDLE_MARGIN_PX};
  }
`;
const CollapseHead = styled('div')`
  display: flex;
  align-items: flex-start;
  margin-bottom: 22px;
  color: ${({ theme }) => theme.palette.common.gray};
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('md')} {
    align-items: center;
  }
`;
const PathwayTitle = styled(Typography)`
  position: relative;
  top: -5px;
  font-size: 14px;
  margin-right: ${TINY_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};

  ${({ theme }) => theme.breakpoints.up('md')} {
    top: 0;
  }
`;
const PathwayName = styled(Link)`
  color: ${({ theme }) => theme.palette.common.white};
  font-weight: 700;
`;
const StyledChevron = styled(ChevronTopIcon)`
  position: relative;
  top: -3px;
  width: 18px;
  min-width: 18px;
  margin-left: auto;
  transform: rotate(${({ reversed }) => (reversed ? -180 : 0)}deg);
  color: ${({ theme }) => theme.palette.common.card.secondary};
  transition: transform 0.3s;
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('md')} {
    top: 0;
  }
`;
const Divider = styled('div')`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => alpha(theme.palette.common.blue, 0.33)};
  margin: ${SMALL_MARGIN_PX} 0;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: ${SUBMIDDLE_MARGIN_PX} 0;
  }
`;
const StyledPathwaySmallIcon = styled(PathwaySmallIcon)`
  position: relative;
  top: -3px;
  width: 18px;
  min-width: 18px;
  height: 18px;
  margin-right: ${TINY_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.accent};
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('md')} {
    top: 0;
  }
`;
const JoinNowBlockContainer = styled(Container)`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const ListOfVideosWrapper = styled('div')`
  position: relative;

  &:after {
    content: '';
    position: absolute;
    right: -1px;
    top: 0;
    z-index: 10;
    width: 75px;
    height: 100%;
    background: linear-gradient(
      270deg,
      ${({ theme }) => theme.palette.common.card.overlay.main} 0%,
      ${({ theme }) => alpha(theme.palette.common.card.overlay.main, 0)} 78.65%
    );
    pointer-events: none;
  }

  & .swiper-button-prev,
  & .swiper-button-next {
    height: 82px;
    transform: translateY(0);
    top: 0;
    margin-top: 0;
    transition: 0.3s opacity;

    &:after {
      height: 24px;
      font-size: 24px;
      font-weight: 700;
    }

    &.swiper-button-disabled {
      opacity: 0;
    }
  }
`;
const ErrorWrapper = styled(Box)`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Wrapper = styled(Box)`
  min-height: 650px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    min-height: calc(100vh - 40px);
  }
`;
const VideoWrap = styled(Container)`
  position: relative;
  z-index: 10;
`;
const VideoBlock = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isXApiPage' && prop !== 'hasSubscription',
})<{
  isXApiPage: boolean;
  hasSubscription: boolean;
}>`
  position: relative;
  z-index: 10;
  margin-bottom: ${({ isXApiPage, hasSubscription }) =>
    isXApiPage || hasSubscription ? MIDDLE_MARGIN_PX : '0px'};
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: ${({ isXApiPage, hasSubscription }) =>
      isXApiPage || hasSubscription ? LARGE_MARGIN_PX : '0px'};
  }
`;
const BlockWrap = styled('div')`
  width: 100%;
`;
const StyledHeader = styled(HeaderWithExpert)`
  margin-top: 115px;
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: 155px;
  }
`;
